import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { BrandingProvider } from '@/context/BrandingContext';
import LivingBackground from '@/components/LivingBackground';
import InstallAppPrompt from '@/components/InstallAppPrompt';
import ProtectedRoute from '@/components/ProtectedRoute';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import DashboardLayout from '@/components/DashboardLayout';
import { ImpersonationBanner } from '@/components/RoleImpersonator';
import CloakWriteConfirmModal from '@/components/CloakWriteConfirmModal';
import HoldingSetupWizard from '@/components/HoldingSetupWizard';
import MatrixScreensaver from '@/components/MatrixScreensaver';

import Home from '@/pages/Home';
import RootRedirect from '@/components/RootRedirect';
import Pricing from '@/pages/Pricing';
import BillingSuccess from '@/pages/BillingSuccess';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import RequestAccess from '@/pages/RequestAccess';
import FounderApply from '@/pages/FounderApply';
import ThemeProbePublic from '@/pages/ThemeProbePublic';
import AuthCallback from '@/pages/AuthCallback';
import MagicLinkLanding from '@/pages/MagicLinkLanding';
import ForgotPassword from '@/pages/ForgotPassword';
import RecoveryLanding from '@/pages/RecoveryLanding';
import RevokeDeviceLanding from '@/pages/RevokeDeviceLanding';
import Collective from '@/pages/Collective';
import TrustCenter from '@/pages/TrustCenter';
import InvestorShare from '@/pages/InvestorShare';
import StartTrial from '@/pages/StartTrial';
import NotFound from '@/pages/NotFound';
import EcosystemGateway from '@/pages/EcosystemGateway';

import DashboardHome from '@/pages/dashboard/DashboardRouter';
import GlobalSearch from '@/pages/dashboard/GlobalSearch';
import DashStartups from '@/pages/dashboard/Startups';
import DashStartupDetail from '@/pages/dashboard/StartupDetail';
import DashStartupRoadmap from '@/pages/dashboard/StartupRoadmap';
import DashInvestors from '@/pages/dashboard/Investors';
import DashChat from '@/pages/dashboard/Chat';
import DashMeetings from '@/pages/dashboard/Meetings';
import DashAgreements from '@/pages/dashboard/Agreements';
import DashEntities from '@/pages/dashboard/Entities';
import DashEvents from '@/pages/dashboard/Events';
import DashUsers from '@/pages/dashboard/UsersAdmin';
import DashSubscriptions from '@/pages/dashboard/SubscriptionsAdmin';
import DashRolesAdmin from '@/pages/dashboard/RolesAdmin';
import DashSandboxAdmin from '@/pages/dashboard/SandboxAdmin';
import DashEntitiesV2 from '@/pages/dashboard/EntitiesDashboard';
import DashEntitiesLive from '@/pages/dashboard/EntitiesLive';
import DashEntityDetail from '@/pages/dashboard/EntityDetail';
import DashOpportunities from '@/pages/dashboard/Opportunities';
import DashApprovals from '@/pages/dashboard/Approvals';
import DashContentStudio from '@/pages/dashboard/ContentStudio';
import DashIntegrations from '@/pages/dashboard/Integrations';
import DashMinutes from '@/pages/dashboard/BoardMinutes';
import DashBankSettings from '@/pages/dashboard/BankSettings';
import DashTenantSettings from '@/pages/dashboard/TenantSettings';
import DashFounderJourney from '@/pages/dashboard/FounderJourney';
import DashFounderVetting from '@/pages/dashboard/FounderVetting';
import DashFounderTimeline from '@/pages/dashboard/FounderTimeline';
import DashFoundersList from '@/pages/dashboard/FoundersList';
import DashPsychoQuiz from '@/pages/dashboard/PsychoQuiz';
import DashPsychoReport from '@/pages/dashboard/PsychoReportView';
import DashPsychoCohort from '@/pages/dashboard/PsychoCohort';
import DashProfile from '@/pages/dashboard/Profile';
import DashTrustedDevices from '@/pages/dashboard/TrustedDevices';
import DashThemeProbe from '@/pages/dashboard/ThemeProbe';
import DashAuditLog from '@/pages/dashboard/AuditLog';
import DashCloakedWrites from '@/pages/dashboard/CloakedWritesAudit';
import DashTenantsAdmin from '@/pages/dashboard/TenantsAdmin';
import DashFormDesigner from '@/pages/dashboard/admin/FormDesigner';
import DashFormFieldsEditor from '@/pages/dashboard/admin/FormFieldsEditor';
import DashAccessRequests from '@/pages/dashboard/admin/AccessRequestsAdmin';
import DashAdminSecurity from '@/pages/admin/AdminSecurity';
import AdminPricing from '@/pages/admin/AdminPricing';
import ScreensaverAdmin from '@/pages/dashboard/ScreensaverAdmin';
import AwsConnection from '@/pages/dashboard/AwsConnection';
import AzureConnection from '@/pages/dashboard/AzureConnection';
import ArchitectureDocs from '@/pages/dashboard/ArchitectureDocs';
import TenantBranding from '@/pages/dashboard/TenantBranding';
import PrivacyCenter from '@/pages/dashboard/PrivacyCenter';
import GovAudit from '@/pages/dashboard/GovAudit';
import SecurityPage from '@/pages/Security';
import ModulesFull from '@/pages/ModulesFull';
import DashEcosystemInsights from '@/pages/dashboard/EcosystemInsights';
import DashAIEmployees from '@/pages/dashboard/AIEmployees';
import DashAIQualifications from '@/pages/dashboard/AIQualifications';
import DashAIEmployeeChat from '@/pages/dashboard/AIEmployeeChat';
import DashPersonaLessons from '@/pages/dashboard/PersonaLessons';
import DashAICouncil from '@/pages/dashboard/AICouncil';
import DashCouncilDetail from '@/pages/dashboard/CouncilSessionDetail';
import CouncilObserve from '@/pages/CouncilObserve';
import DashAIDrafts from '@/pages/dashboard/AIDrafts';
import DashAIMentors from '@/pages/dashboard/AIMentors';
import DashLibrary from '@/pages/dashboard/Library';
import DashUserGuides from '@/pages/dashboard/UserGuides';
import DashAPIDocs from '@/pages/dashboard/APIDocs';
import DashChangeRequests from '@/pages/dashboard/ChangeRequests';
import DashFeedbackJournal from '@/pages/dashboard/FeedbackJournal';
import DashERMKickstart from '@/pages/dashboard/ERMKickstart';
import DashFinanceEngine from '@/pages/dashboard/FinanceEngine';
import DashSecurityCenter from '@/pages/dashboard/SecurityCenter';
import DashDocumentsHub from '@/pages/dashboard/DocumentsHub';
import DashOperatingManual from '@/pages/dashboard/OperatingManual';
import InviteAccept from '@/pages/InviteAccept';
import DashShuraNexus from '@/pages/dashboard/ShuraNexus';
import DashTasks from '@/pages/dashboard/Tasks';
import DashTasksInbox from '@/pages/dashboard/TasksInbox';

function PublicShell({ children }) {
  return (
    <>
      <PublicHeader />
      {children}
      <PublicFooter />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth fragment synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      {/* Cloak warning bar — hoisted above <Routes> so it appears even on
          the "Access restricted" page that ProtectedRoute renders when a
          cloaked Maker is previewing as a less-privileged role. Without
          this hoist, the only escape hatch (the banner) was unreachable
          because DashboardLayout never mounted. */}
      <ImpersonationBanner />
      <CloakWriteConfirmModal />
      <HoldingSetupWizard />
      {/* Idle privacy screensaver — logged-in users only, admin-tunable
          at /dashboard/admin/screensaver. Renders nothing until idle. */}
      <MatrixScreensaver />
      <Routes>
      {/* ── Public marketing surface (Phase 2) ──
          All marketing/landing entry points funnel into the unified
          Ecosystem Gateway. Old /platform, /modules, /solutions, /industries,
          /home are killed and redirect to the gateway. Pricing stays so the
          Stripe checkout flow can deep-link customers in. */}
      <Route path="/" element={<EcosystemGateway />} />
      <Route path="/home" element={<EcosystemGateway />} />
      <Route path="/platform" element={<EcosystemGateway />} />
      <Route path="/modules" element={<EcosystemGateway />} />
      <Route path="/modules/:pillarId" element={<EcosystemGateway />} />
      <Route path="/solutions" element={<EcosystemGateway />} />
      <Route path="/industries" element={<EcosystemGateway />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/modules-full" element={<ModulesFull />} />
      <Route path="/go" element={<RootRedirect />} />

      {/* Public observer link — no auth required, token signed JWT */}
      <Route path="/observe/council/:councilId" element={<CouncilObserve />} />

      <Route path="/pricing" element={<Pricing />} />
      <Route path="/billing/success" element={<BillingSuccess />} />
      <Route path="/start-trial" element={<StartTrial />} />
      <Route path="/invite/:token" element={<InviteAccept />} />

      {/* Auth */}
      <Route path="/login" element={<Login />} />
      {/* v266: Signup is re-enabled with international-standard password
          auth (NIST 800-63B). The Maker-reviewed Request Access form is
          kept at its original path for enterprise/waitlist inbound. */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/founder-apply" element={<FounderApply />} />
      <Route path="/__theme-probe" element={<ThemeProbePublic />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/magic-link" element={<MagicLinkLanding />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/auth/recovery" element={<RecoveryLanding />} />
      <Route path="/auth/revoke-device" element={<RevokeDeviceLanding />} />
      <Route path="/collective" element={<Collective />} />
      <Route path="/trust" element={<TrustCenter />} />
      {/* Public investor magic-link share — no auth, token IS the auth */}
      <Route path="/share/dd/:token" element={<InvestorShare />} />

      {/* Authenticated dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardLayout><DashboardHome /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/startups" element={
        <ProtectedRoute><DashboardLayout><DashStartups /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/startups/:id" element={
        <ProtectedRoute><DashboardLayout><DashStartupDetail /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/startups/:startupId/roadmap" element={
        <ProtectedRoute><DashboardLayout><DashStartupRoadmap /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/investors" element={
        <ProtectedRoute><DashboardLayout><DashInvestors /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/chat" element={
        <ProtectedRoute><DashboardLayout><DashChat /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/meetings" element={
        <ProtectedRoute><DashboardLayout><DashMeetings /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/agreements" element={
        <ProtectedRoute><DashboardLayout><DashAgreements /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/entities" element={
        <ProtectedRoute><DashboardLayout><DashEntities /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/entities-os" element={
        <ProtectedRoute><DashboardLayout><DashEntitiesV2 /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/entities-live" element={
        <ProtectedRoute><DashboardLayout><DashEntitiesLive /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/entities-os/:id" element={
        <ProtectedRoute><DashboardLayout><DashEntityDetail /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/opportunities" element={
        <ProtectedRoute><DashboardLayout><DashOpportunities /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/shura" element={
        <ProtectedRoute><DashboardLayout><DashShuraNexus /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/tasks" element={
        <ProtectedRoute><DashboardLayout><DashTasks /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/tasks/inbox" element={
        <ProtectedRoute><DashboardLayout><DashTasksInbox /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/approvals" element={
        <ProtectedRoute><DashboardLayout><DashApprovals /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/approvals/:id" element={
        <ProtectedRoute><DashboardLayout><DashApprovals /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/content-studio" element={
        <ProtectedRoute><DashboardLayout><DashContentStudio /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/integrations" element={
        <ProtectedRoute><DashboardLayout><DashIntegrations /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founder-vetting" element={
        <ProtectedRoute><DashboardLayout><DashFounderVetting /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founder-vetting/:assessmentId" element={
        <ProtectedRoute><DashboardLayout><DashFounderVetting /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founders" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashFoundersList /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founders/:userId" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashFounderTimeline /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/startups/:id/psycho-quiz" element={
        <ProtectedRoute><DashboardLayout><DashPsychoQuiz /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/psycho-reports/:reportId" element={
        <ProtectedRoute><DashboardLayout><DashPsychoReport /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/psycho-cohort" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashPsychoCohort /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/profile" element={
        <ProtectedRoute><DashboardLayout><DashProfile /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/security/trusted-devices" element={
        <ProtectedRoute><DashboardLayout><DashTrustedDevices /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/__theme-probe" element={
        <ProtectedRoute><DashboardLayout><DashThemeProbe /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/minutes" element={
        <ProtectedRoute><DashboardLayout><DashMinutes /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/minutes/:id" element={
        <ProtectedRoute><DashboardLayout><DashMinutes /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/events" element={
        <ProtectedRoute><DashboardLayout><DashEvents /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/users" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashUsers /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/subscriptions" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashSubscriptions /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/roles" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashRolesAdmin /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/sandbox" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashSandboxAdmin /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/billing/bank" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashBankSettings /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/tenant" element={
        <ProtectedRoute><DashboardLayout><DashTenantSettings /></DashboardLayout></ProtectedRoute>
      } />
      {/* Alias — the header user-menu "Settings" item points here. Without
          this route the click landed on the 404 page. */}
      <Route path="/dashboard/settings" element={
        <ProtectedRoute><DashboardLayout><DashTenantSettings /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founder" element={
        <ProtectedRoute><DashboardLayout><DashFounderTimeline /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/founder-legacy" element={
        <ProtectedRoute><DashboardLayout><DashFounderJourney /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/audit" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashAuditLog /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/audit/cloaked-writes" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><DashCloakedWrites /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/tenants" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><DashTenantsAdmin /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/forms" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashFormDesigner /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/forms/:formKey" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashFormFieldsEditor /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/access-requests" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashAccessRequests /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/search" element={
        <ProtectedRoute><DashboardLayout><GlobalSearch /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/security" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashAdminSecurity /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/pricing" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><AdminPricing /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/screensaver" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><ScreensaverAdmin /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/aws" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><AwsConnection scope="platform" /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/settings/aws" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin', 'incubator_admin', 'investor', 'founder']}><DashboardLayout><AwsConnection scope="tenant" /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/admin/azure" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><AzureConnection scope="platform" /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/settings/azure" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin', 'incubator_admin', 'investor', 'founder']}><DashboardLayout><AzureConnection scope="tenant" /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/docs" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin', 'incubator_admin', 'investor', 'founder']}><DashboardLayout><ArchitectureDocs /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/branding" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin', 'incubator_admin']}><DashboardLayout><TenantBranding /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/privacy" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><PrivacyCenter /></DashboardLayout></ProtectedRoute>
      }/>
      <Route path="/dashboard/gov/audit" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><GovAudit /></DashboardLayout></ProtectedRoute>
      }/>
      <Route path="/dashboard/insights" element={
        <ProtectedRoute><DashboardLayout><DashEcosystemInsights /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-employees" element={
        <ProtectedRoute><DashboardLayout><DashAIEmployees /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-employees/qualifications" element={
        <ProtectedRoute><DashboardLayout><DashAIQualifications /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-employees/:personaId/lessons" element={
        <ProtectedRoute><DashboardLayout><DashPersonaLessons /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-employees/:personaId" element={
        <ProtectedRoute><DashboardLayout><DashAIEmployeeChat /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-council" element={
        <ProtectedRoute><DashboardLayout><DashAICouncil /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-council/:councilId" element={
        <ProtectedRoute><DashboardLayout><DashCouncilDetail /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-drafts" element={
        <ProtectedRoute><DashboardLayout><DashAIDrafts /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/ai-mentors" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><DashAIMentors /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/library" element={
        <ProtectedRoute roles={['system_admin']}><DashboardLayout><DashLibrary /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/help" element={
        <ProtectedRoute><DashboardLayout><DashUserGuides /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/api-docs" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashAPIDocs /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/change-requests" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashChangeRequests /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/feedback-journal" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashFeedbackJournal /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/erm" element={
        <ProtectedRoute><DashboardLayout><DashERMKickstart /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/finance" element={
        <ProtectedRoute><DashboardLayout><DashFinanceEngine /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/security" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashSecurityCenter /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/documents" element={
        <ProtectedRoute><DashboardLayout><DashDocumentsHub /></DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboard/manual" element={
        <ProtectedRoute roles={['holding_admin', 'system_admin']}><DashboardLayout><DashOperatingManual /></DashboardLayout></ProtectedRoute>
      } />

      <Route path="*" element={<PublicShell><NotFound /></PublicShell>} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <BrandingProvider>
            <LivingBackground />
            <AppRouter />
            <InstallAppPrompt />
            <Toaster position="top-center" richColors offset={20} />
          </BrandingProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
